import React, { forwardRef } from 'react';
import styled from 'styled-components/native';
import { Controller } from 'react-hook-form';
import {
  Platform,
  View,
  Switch,
  TextInput as BaseTextInput,
  Pressable,
} from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareXmark } from '@fortawesome/free-solid-svg-icons';

import { Text, Hint } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import fonts from 'app/src/styles/fonts';

import { Picker } from './Picker';
import { DateTimePicker } from './DateTimePicker';
import { abbreviations } from './states';
import moment from 'moment';

export const StyledTextInput = styled(BaseTextInput)`
  border-width: 1px;
  border-color: black;
  padding: 6px;
  background-color: #fff;
`;

export const StyledPicker = styled(Picker)`
  border-color: black;
  padding: 3px;
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 14px;
`;

const ErrorText = styled(Text)`
  padding-left: 10px;
  color: red;
`;

export const Field = styled(View)`
  margin-bottom: 10px;
`;

export const SwitchField = styled(View)`
  flex-direction: row;
  margin-top: 5px;
  margin-bottom: 10px;
  align-items: center;
`;

const SwitchLabel = styled(Text)`
  margin-left: 10px;
`;

export const FieldLabel = ({ name, label, hint, errors }) => {
  let errorMessage;

  if (errors?.[name]) {
    errorMessage = errors[name].message || 'Required';
  }

  return (
    <>
      <View style={{flexDirection: 'row'}}>
        <Text>{label}</Text>
        {errorMessage && <ErrorText>{errorMessage}.</ErrorText>}
      </View>
      {hint && <Hint>{hint}</Hint>}
    </>
  );
};

export const TextInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
          />
        )}
      />
    </Field>
  );
});

export const CheckboxInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    checked,
    ...controllerProps
  } = props;

  return (
    <Field>
      <View style={{display: 'flex', flexDirection: 'row'}}>
        <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <input
              type='checkbox'
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </View>
    </Field>
  );
});

export const DateTimeInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => {
          const onChange = (e, valueOnNative) => {
            if ('web' === Platform.OS) {
              field.onChange(e.target.value);
            } else {
              field.onChange(valueOnNative);
            }
          };
          return (
            <DateTimePicker
              {...field}
              type="datetime-local"
              mode="datetime"
              onChange={onChange}
              ref={ref}
            />
          );
        }}
      />
    </Field>
  );
});

export const DateInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    register,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => {
          const onChange = (e, valueOnNative) => {
            if ('web' === Platform.OS) {
              field.onChange(e.target.value);
            } else {
              field.onChange(valueOnNative);
            }
          };
          return (
            <DateTimePicker
              {...field}
              type="date"
              mode="date"
              onChange={onChange}
              ref={ref}
            />
          );
        }}
      />
    </Field>
  );
});

export const PickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {options.map(option => (
              <Picker.Item
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const StatePickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            <Picker.Item
              key='default-null'
              value=""
              label="Pick a State"
            />
            {abbreviations.map(option => (
              <Picker.Item
                key={option}
                value={option}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const SwitchInput = props => {
  const {
    label,
    control,
    ...controllerProps
  } = props;

  return (
    <SwitchField>
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <Switch
            {...field}
            activeTrackColor={colors.lightButton}
            activeThumbColor={colors.button}
            onValueChange={field.onChange}
          />
        )}
      />
      <SwitchLabel>{label}</SwitchLabel>
    </SwitchField>
  );
};

export const FormattedPhoneInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
          />
        )}
      />
    </Field>
    // const standardizedPhone = value => {
    //   if (value) {
    //     return '+1' + value.replace(/[-()/\s+/]/g, '');
    //   }

    //   return null;
    // };
  )
})

export const RolePickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {['Admin','Accounting','Lot'].map(option => (
              <Picker.Item
                key={option}
                value={option}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const YearPickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  let finalYear = 2099; // The customer requested this be hard-coded.
  let years = [];
  for(let x = 0; x < 200; x++) {
    years.push(finalYear - x);
  }

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {years.map(option => (
              <Picker.Item
                key={`year${option}`}
                value={parseInt(option)}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const StatusPickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {STATUSES.map(option => (
              <Picker.Item
                key={`status${option}`}
                value={option}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

const MultiSelectInput = (props) => {
  const {selected, setSelected, resourceType, resources} = props;
  const filteredResources = resources?.filter(
    resource => !selected?.includes(resource)
  ) || []

  const UsersContainer = styled(View)`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    width: 600px;
  `;

  const UserButtonsContainer = styled(View)`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    border: solid 4px ${colors.elGray}
    border-radius: 5px;
    padding: 20px;
    overflow-x: wrap;
  `;

  const UserSelectCard = styled(View)`
    border: 1px solid black;
    width: fit-content;
    padding: 5px;
    border-radius: 5px;
    margin: 5px;
    display: flex;
    flex-direction: row;
    background-color: #fff;
  `;

  const LabelContainer = styled(View)`
    display: flex;
    flex-direction: row;
  `;

  const InputLabel = styled(Text)`
    font-weight: bold;
    font-size: 18px;
    color: ${colors.elRed}
  `;

  const addToArray = (resource) => {
    let newSelected = [...selected, resource.id]
    setSelected(newSelected);
  }

  const removeFromArray = (clickedResourceId) => {
    let newSelected = selected?.filter(resourceId => {
      if(resourceId !== clickedResourceId) {
        return resourceId;
      }
    })
    setSelected(newSelected);
  }

  return (
    <View
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        padding: '10px',
        border: '1px solid black'
      }}
    >
      <LabelContainer>
        <InputLabel>{resourceType}</InputLabel>
        {!selected?.length ?
          <View style={{marginLeft: '5px', marginTop: '2px'}}>
            <Text>(click on the names you wish to add)</Text>
          </View> :
          <Pressable
            onPress={() => setSelected([])}
            style={{marginLeft: '5px', marginTop: '2px'}}
          >
            <Text>clear all</Text>
          </Pressable>
        }
      </LabelContainer>
      <UsersContainer>
        {resources && resources.length ?
          resources?.map(resource => {
            if(selected.includes(resource.id)) {
              return (
                <UserSelectCard key={resource.id}>
                  <Text>{resource.name}</Text>
                  <Pressable>
                    <FontAwesomeIcon
                      icon={faSquareXmark}
                      size='lg'
                      style={{
                        color: colors.elRed,
                        marginLeft: '5px'
                      }}
                      onClick={() => removeFromArray(resource.id)}
                    />
                  </Pressable>
                </UserSelectCard>
              )
            }
          }) :
          null
        }
      </UsersContainer>
      {filteredResources && filteredResources.length > 0 &&
        <UserButtonsContainer>
          {filteredResources?.map(resource => {
            if(!selected.includes(resource.id)) {
              return (
                <View key={resource.id}>
                  <Pressable
                    onPress={() => addToArray(resource)}
                  >
                    <UserSelectCard>
                      <Text>{resource.name}</Text>
                    </UserSelectCard>
                  </Pressable>
                </View>
              )
            }
          })}
        </UserButtonsContainer>
      }
    </View>
  )
};

const Input = {
  Text: TextInput,
  Date: DateInput,
  DateTime: DateTimeInput,
  Picker: PickerInput,
  StatePicker: StatePickerInput,
  Switch: SwitchInput,
  Phone: FormattedPhoneInput,
  RolePicker: RolePickerInput,
  YearPicker: YearPickerInput,
  StatusPicker: StatusPickerInput,
  Checkbox: CheckboxInput,
  MultiSelect: MultiSelectInput
};

export default Input;
